import { AppConfig } from '@core/domain/AppConfig.ts';
import { markPendingCatalogSyncAfterUpdate } from '@core/domain/sessionEvents.ts';
import { clearLocalSqliteIdbDataPreserveMedia } from '@src/modules/platform/local-sqlite/infrastructure/sqliteIdbStorage.ts';
import type {
  AppMaintenancePlatform,
  AppMaintenancePort,
  AppUpdateCheckResult,
  AppVersionInfo,
  HardRefreshOptions,
} from '../../domain/ports/AppMaintenancePort.ts';
import {
  parseMobileVersionManifest,
  resolveAppUpdateCheck,
} from '../../domain/mobileVersionManifest.ts';
import { clearSnapshotRevisionLocalStorageKeys } from '@src/modules/platform/local-sqlite/infrastructure/snapshotRevisionGuard.ts';
import { reloadApp } from '@src/modules/platform/capacitor/infrastructure/capacitorAppReload.ts';
import { capacitorHttpGetJson } from '@src/modules/platform/capacitor/infrastructure/capacitorHttpGetJson.ts';

function parseVersionCode(raw: string | undefined): number {
  const n = Number.parseInt(String(raw ?? ''), 10);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

function readLocalAppVersion(): AppVersionInfo {
  return {
    versionCode: parseVersionCode(import.meta.env.APP_VERSION_CODE),
    versionName: (import.meta.env.APP_VERSION_NAME ?? '').trim() || '1.0.0',
  };
}

async function fetchRemoteVersionManifest(url: string): Promise<AppUpdateCheckResult> {
  const local = readLocalAppVersion();
  try {
    const res = await capacitorHttpGetJson(url);
    if (!res.ok) {
      return resolveAppUpdateCheck(local, null, `Manifesto HTTP ${res.status}`);
    }
    const remote = parseMobileVersionManifest(res.data);
    return resolveAppUpdateCheck(local, remote);
  } catch (e) {
    const reason = e instanceof Error ? e.message : String(e);
    return resolveAppUpdateCheck(local, null, reason);
  }
}

/** Runtime platform: Capacitor native → apk; service worker enabled → pwa; else web. */
export function detectAppMaintenancePlatform(): AppMaintenancePlatform {
  const cap = (globalThis as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  if (cap?.isNativePlatform?.()) return 'apk';
  if ((import.meta.env.ADMIN_OFFLINE_SERVICE_WORKER ?? '1') !== '0') return 'pwa';
  return 'web';
}

async function unregisterAllServiceWorkers(): Promise<void> {
  const sw = globalThis.navigator?.serviceWorker;
  if (!sw) return;
  const reg = await sw.getRegistration();
  if (reg) await reg.unregister();
}

async function deleteAllCaches(): Promise<void> {
  if (!globalThis.caches) return;
  const keys = await caches.keys();
  await Promise.all(keys.map((key) => caches.delete(key)));
}

function clearWebStorageExceptNothing(): void {
  try {
    globalThis.localStorage?.clear();
  } catch {
    /* ignore */
  }
  try {
    globalThis.sessionStorage?.clear();
  } catch {
    /* ignore */
  }
}

/** PWA / web hard refresh: SW unregister, Cache Storage, optional IndexedDB, storage wipe, reload. */
export class PwaAppMaintenanceAdapter implements AppMaintenancePort {
  getPlatform() {
    return detectAppMaintenancePlatform();
  }

  getMobileStoreUrl(): string | null {
    const url = (import.meta.env.ADMIN_MOBILE_STORE_URL ?? '').trim();
    return url || null;
  }

  getLocalAppVersion(): AppVersionInfo {
    return readLocalAppVersion();
  }

  async checkForAppUpdate(): Promise<AppUpdateCheckResult> {
    const url = (import.meta.env.ADMIN_MOBILE_VERSION_MANIFEST_URL ?? '').trim();
    if (!url) {
      return resolveAppUpdateCheck(readLocalAppVersion(), null, 'Sin URL de manifesto de versión.');
    }
    return fetchRemoteVersionManifest(url);
  }

  async hardRefresh(options: HardRefreshOptions): Promise<void> {
    await unregisterAllServiceWorkers();
    await deleteAllCaches();
    clearWebStorageExceptNothing();
    clearSnapshotRevisionLocalStorageKeys();
    markPendingCatalogSyncAfterUpdate();
    if (options.clearIndexedDb && AppConfig.useLocalSqliteClient()) {
      await clearLocalSqliteIdbDataPreserveMedia();
    }
    await reloadApp();
  }
}

export function createAppMaintenancePort(): AppMaintenancePort {
  return new PwaAppMaintenanceAdapter();
}
